import { formatIsoDate } from "../../../../../lib/format";
import type { CallScriptSaveWarning } from "../api/callScriptShared.types";
import { PHONE_NUMBER_DIGITS } from "../constants";
import { formatPhoneDisplay, formatPhoneOrDigits } from "../fields/PhoneNumberInput";

// ---------------------------------------------------------------------------
// callScriptComparison
//
// Pure logic shared by both Call Script modals' "Verified Information" vs
// "Saved Information" comparison: which rows count as changed, how a value
// renders in the save-confirmation table, and how save warnings group.
//
// No JSX and no i18n — every string in or out is passed by the caller, so
// neither screen's translation namespace leaks in here. Generic over the
// field-key type so each modal keeps its own strongly-typed field model.
//
// The shared control renderer (CallScriptFieldControl) lives in its own file
// rather than here, since it needs JSX, but is built on the same
// ComparisonRowKind union this file defines.
// ---------------------------------------------------------------------------

/**
 * The union of control kinds across both scripts. It is a superset on purpose:
 * `formatRowValueForDisplay` switches on kind and must handle every case even
 * though each screen only uses a subset (employment has `checkbox`/`number`,
 * residence has `yesNoBlank`).
 */
export type ComparisonRowKind =
  | { kind: "text"; placeholder?: string; maxLength?: number }
  | { kind: "phone"; placeholder?: string }
  | { kind: "number" }
  | { kind: "currency" }
  | { kind: "date" }
  | { kind: "checkbox" }
  | { kind: "yesNo" }
  | { kind: "yesNoBlank" }
  | {
      kind: "select";
      options: ReadonlyArray<{ value: string; label: string }>;
      allowFreeText?: boolean;
      /** The dropdown's empty-selection placeholder (employment's rows set "Select"; residence leaves this unset). */
      placeholder?: string;
    };

/** One comparison row: a field key, its label, and the control to render. Each modal intersects its own extras onto this. */
export type ComparisonRow<TKey extends string> = { key: TKey; label: string } & ComparisonRowKind;

/**
 * A {@link ComparisonRow} narrowed to the control kinds one screen actually
 * uses, so a row type can't claim a kind its control renderer doesn't handle.
 */
export type ComparisonRowOfKinds<TKey extends string, TKind extends ComparisonRowKind["kind"]> = {
  key: TKey;
  label: string;
} & Extract<ComparisonRowKind, { kind: TKind }>;

/** Any value a comparison row can hold, across both scripts' field models. */
export type ComparisonValue = string | number | boolean | undefined;

/** One row of the save-confirmation dialog's changed-fields table. */
export interface ChangedFieldRow<TKey extends string> {
  key: TKey;
  label: string;
  verifiedDisplay: string;
  savedDisplay: string;
}

/**
 * Whether a row's Verified value counts as "entered". Yes/No and checkbox rows
 * default to their unset state, everything else to blank.
 * @param {ComparisonRow<string>} row - The row definition.
 * @param {ComparisonValue} value - The current Verified value.
 * @returns {boolean} True once the analyst has supplied a value.
 */
export function isRowValueEntered(row: ComparisonRow<string>, value: ComparisonValue): boolean {
  if (row.kind === "checkbox" || row.kind === "yesNo") {
    return value === true;
  }
  return value !== "" && value !== undefined;
}

/** Per-screen tweaks to {@link isRowChanged}. */
export interface IsRowChangedOptions<TKey extends string> {
  /**
   * Rows with no Saved counterpart to compare against, which therefore count
   * as changed the moment anything is entered. Employment passes its
   * `NO_SAVED_COUNTERPART_FIELDS` membership test; residence has no such rows.
   */
  alwaysChangedWhenEntered?: (key: TKey) => boolean;
}

/**
 * Whether a row renders its live "changed" highlight: the analyst entered
 * something and it differs from Saved. Phone rows compare on the normalized
 * digits rather than the raw string — the Saved side can arrive dashed while
 * Verified is raw digits, and a plain `!==` would flag re-entering the
 * identical number as changed.
 * @template TKey - The screen's field-key union.
 * @param {ComparisonRow<TKey>} row - The row definition.
 * @param {ComparisonValue} verifiedValue - The current Verified value.
 * @param {ComparisonValue} savedValue - The Saved value to compare against.
 * @param {IsRowChangedOptions<TKey>} [options] - Per-screen tweaks.
 * @returns {boolean} True when the row should be highlighted.
 */
export function isRowChanged<TKey extends string>(
  row: ComparisonRow<TKey>,
  verifiedValue: ComparisonValue,
  savedValue: ComparisonValue,
  options?: IsRowChangedOptions<TKey>
): boolean {
  if (!isRowValueEntered(row, verifiedValue)) {
    return false;
  }
  if (options?.alwaysChangedWhenEntered?.(row.key)) {
    return true;
  }
  if (row.kind === "phone") {
    return formatPhoneOrDigits(String(verifiedValue)) !== formatPhoneOrDigits(String(savedValue));
  }
  return verifiedValue !== savedValue;
}

// ---------------------------------------------------------------------------
// Per-kind display formatters
//
// One function per way a value can render, so formatRowValueForDisplay stays a
// flat dispatch. Splitting them out also makes two exact duplications visible
// that the old inline switch hid: `yesNoBlank` and `number` formatted
// identically, as did `select`'s no-match fallback and the `text` default.
// ---------------------------------------------------------------------------

/**
 * The value as typed, or the placeholder when it is blank or not a string.
 * Used by the kinds whose value is always a string.
 * @param {ComparisonValue} value - The value to render.
 * @param {string} noValueLabel - Placeholder for a blank value.
 * @returns {string} The display string.
 */
function stringOrPlaceholder(value: ComparisonValue, noValueLabel: string): string {
  return typeof value === "string" && value !== "" ? value : noValueLabel;
}

/**
 * The value stringified, or the placeholder when unset. Used by the kinds whose
 * value can be a number — unlike {@link stringOrPlaceholder}, a numeric 0 still
 * renders as "0" rather than falling back to the placeholder.
 * @param {ComparisonValue} value - The value to render.
 * @param {string} noValueLabel - Placeholder for an unset value.
 * @returns {string} The display string.
 */
function scalarOrPlaceholder(value: ComparisonValue, noValueLabel: string): string {
  return value === "" || value === undefined ? noValueLabel : String(value);
}

/**
 * MM/DD/YYYY, or the placeholder when no ISO date was captured.
 * @param {ComparisonValue} value - The raw ISO date.
 * @param {string} noValueLabel - Placeholder for a blank value.
 * @returns {string} The display string.
 */
function dateOrPlaceholder(value: ComparisonValue, noValueLabel: string): string {
  return typeof value === "string" && value !== "" ? formatIsoDate(value) : noValueLabel;
}

/**
 * `$1,234.50`, or the placeholder when no amount was captured.
 * @param {ComparisonValue} value - The raw amount.
 * @param {string} noValueLabel - Placeholder for an absent amount.
 * @returns {string} The display string.
 */
function currencyOrPlaceholder(value: ComparisonValue, noValueLabel: string): string {
  if (typeof value !== "number") {
    return noValueLabel;
  }
  return `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * The dialed number in NANP form, or the placeholder when no digits were entered.
 * @param {ComparisonValue} value - The raw phone value.
 * @param {string} noValueLabel - Placeholder for a blank value.
 * @returns {string} The display string.
 */
function phoneOrPlaceholder(value: ComparisonValue, noValueLabel: string): string {
  if (typeof value !== "string" || value.trim() === "") {
    return noValueLabel;
  }
  return formatPhoneDisplay(value.replaceAll(/\D/gu, "").slice(0, PHONE_NUMBER_DIGITS));
}

/**
 * The matching option's label, falling back to the raw value when the saved
 * value isn't in the option list (e.g. a code the catalog no longer carries).
 * @param {ReadonlyArray<{ value: string; label: string }>} options - The select's options.
 * @param {ComparisonValue} value - The value to look up.
 * @param {string} noValueLabel - Placeholder for a blank value.
 * @returns {string} The display string.
 */
function selectOptionOrPlaceholder(
  options: ReadonlyArray<{ value: string; label: string }>,
  value: ComparisonValue,
  noValueLabel: string
): string {
  const option = options.find((candidate) => candidate.value === value);
  return option ? option.label : stringOrPlaceholder(value, noValueLabel);
}

/**
 * Renders a row's raw value the way the save-confirmation dialog's comparison
 * table displays it.
 * @param {ComparisonRow<string>} row - The row definition.
 * @param {ComparisonValue} value - The value to render.
 * @param {string} noValueLabel - Placeholder for a blank/unanswered value.
 * @returns {string} The display string.
 */
export function formatRowValueForDisplay(
  row: ComparisonRow<string>,
  value: ComparisonValue,
  noValueLabel: string
): string {
  switch (row.kind) {
    case "checkbox": {
      return value === true ? "Yes" : "No";
    }
    case "yesNo": {
      return value === true ? "Y" : "N";
    }
    // Identical formatting — `yesNoBlank` holds "" | "Y" | "N", `number` holds
    // number | "", and both render the raw value unless it is unset.
    case "yesNoBlank":
    case "number": {
      return scalarOrPlaceholder(value, noValueLabel);
    }
    case "date": {
      return dateOrPlaceholder(value, noValueLabel);
    }
    case "currency": {
      return currencyOrPlaceholder(value, noValueLabel);
    }
    case "phone": {
      return phoneOrPlaceholder(value, noValueLabel);
    }
    case "select": {
      return selectOptionOrPlaceholder(row.options, value, noValueLabel);
    }
    default: {
      return stringOrPlaceholder(value, noValueLabel);
    }
  }
}

/** Per-screen tweaks to {@link buildChangedFieldRows}. */
export interface BuildChangedFieldRowsOptions<TKey extends string> {
  /** The placeholder ("—") used for a blank value or a field with nothing saved. */
  noSavedValue: string;
  /** Skips rows currently hidden by the screen's own render conditions — a hidden row can't have been changed. */
  isVisible?: (row: ComparisonRow<TKey>) => boolean;
  /** See {@link IsRowChangedOptions.alwaysChangedWhenEntered}. */
  alwaysChangedWhenEntered?: (key: TKey) => boolean;
  /** False for rows with nothing to compare against — their Saved cell shows `noSavedValue` instead of a formatted value. */
  hasSavedCounterpart?: (key: TKey) => boolean;
}

/**
 * Builds the save-confirmation dialog's "changed" rows: fields whose Verified
 * value differs from Saved, plus (where the screen defines them) fields with
 * no saved counterpart at all, which show whenever anything was entered.
 * @template TKey - The screen's field-key union.
 * @param {ReadonlyArray<ComparisonRow<TKey>>} rows - The ordered row definitions.
 * @param {Record<TKey, ComparisonValue>} verified - The current Verified values.
 * @param {Record<TKey, ComparisonValue>} saved - The Saved values.
 * @param {BuildChangedFieldRowsOptions<TKey>} options - Per-screen tweaks.
 * @returns {ChangedFieldRow<TKey>[]} Only the rows that differ.
 */
export function buildChangedFieldRows<TKey extends string>(
  rows: ReadonlyArray<ComparisonRow<TKey>>,
  verified: Record<TKey, ComparisonValue>,
  saved: Record<TKey, ComparisonValue>,
  options: BuildChangedFieldRowsOptions<TKey>
): Array<ChangedFieldRow<TKey>> {
  const changed: Array<ChangedFieldRow<TKey>> = [];
  for (const row of rows) {
    if (options.isVisible && !options.isVisible(row)) {
      continue;
    }
    const verifiedValue = verified[row.key];
    const savedValue = saved[row.key];
    if (!isRowChanged(row, verifiedValue, savedValue, options)) {
      continue;
    }
    const savedCounterpart = options.hasSavedCounterpart?.(row.key) ?? true;
    changed.push({
      key: row.key,
      label: row.label,
      verifiedDisplay: formatRowValueForDisplay(row, verifiedValue, options.noSavedValue),
      savedDisplay: savedCounterpart
        ? formatRowValueForDisplay(row, savedValue, options.noSavedValue)
        : options.noSavedValue,
    });
  }
  return changed;
}

/** One save-warning group: a code plus that code's `detail` messages, in response order. */
export interface WarningGroup {
  code: string;
  details: string[];
}

/**
 * Groups save-response warnings by `code`, preserving each group's and each
 * detail's original order — so multiple warnings sharing a code render under
 * one heading instead of repeating it.
 * @param {CallScriptSaveWarning[]} warnings - The save response's `warnings` array.
 * @returns {WarningGroup[]} One entry per distinct code, in first-seen order.
 */
export function groupWarningsByCode(warnings: CallScriptSaveWarning[]): WarningGroup[] {
  const groups: WarningGroup[] = [];
  const groupByCode = new Map<string, WarningGroup>();
  for (const warning of warnings) {
    let group = groupByCode.get(warning.code);
    if (!group) {
      group = { code: warning.code, details: [] };
      groupByCode.set(warning.code, group);
      groups.push(group);
    }
    group.details.push(warning.detail);
  }
  return groups;
}
